import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';

const FAQS = [
  {
    question: 'What is the best AI file organizer for real estate?',
    answer:
      "Sortio is built for the real estate document mix: per-property folders for active listings, role-based subfolders for contracts, disclosures, photos, inspection, and closing, plus automatic renaming of camera files (DSC_0001 to property-prefixed names) and content-aware routing for ALTA settlement statements, disclosures, and commission docs. It runs on Mac, Windows, and Linux, with a dotloop integration shipping in the next release.",
  },
  {
    question: 'How does Sortio organize per-property folders automatically?',
    answer:
      "Sortio reads each file (PDF, JPEG, CSV export) and extracts the property address, transaction parties, document type, and date. Files land in a per-property folder named with the address, with role-based subfolders. You can start from an empty Transactions folder or point Sortio at your existing structure; it adapts to whichever pattern you already use.",
  },
  {
    question: 'Does Sortio integrate with dotloop?',
    answer:
      "The dotloop integration is shipping in the next Sortio release alongside Buildium, Rent Manager, and Zotero. It uses dotloop's OAuth 2.0 flow and the loop and folder endpoints to upload files directly into the right loop. Today, Sortio works with dotloop via the filesystem: point it at the local sync folder or your downloads folder and it routes the exports into per-property folders on your Mac.",
  },
  {
    question: 'What software do transaction coordinators use, and how does Sortio fit in?',
    answer:
      "TCs typically use a transaction coordinator software like dotloop, Sky Slope, zipForm, Brokermint, or Paperless Pipeline for compliance workflow and electronic signatures. Sortio fits in two ways: as an upstream organizer for the loose files (inspections, disclosures, vendor invoices, signed amendments) before they go into the transaction platform, and as the file-naming layer that turns scan_001 into a property-prefixed filename a TC can audit at a glance.",
  },
  {
    question: 'Can Sortio handle listing photos from Sony, Canon, and DJI drones?',
    answer:
      "Yes. Sortio reads EXIF metadata (camera model, capture date, GPS when present) and matches photos to the property folder by capture date and shoot context. A mixed shoot with 280 DJI drone files and 180 Sony ILCE-7M4 interior shots routes cleanly into one property folder, renamed with the address. The Rule Builder lets you split drone vs interior into separate subfolders if you upload them separately to the MLS.",
  },
  {
    question: 'How does Sortio rename closing docs like ALTA settlement statements?',
    answer:
      "Closing PDFs are read locally for the property address, buyer and seller names, and closing date. A file named ALTA-Combined-Settlement-Statement.pdf gets renamed to ALTA Settlement Statement - 1127 S Cornell Terrace - 2026-04-12.pdf and routed under that property's Closing subfolder. Seller net sheets, wire confirmations, and commission disbursement statements follow the same pattern.",
  },
  {
    question: 'Will buyer financial info or Social Security numbers leave my machine?',
    answer:
      "Not unless you choose the cloud classifier. In Ollama mode no document content (and no filenames) cross the network. In BYOK mode requests go from your machine directly to your provider on your account, never through Sortio. With the default Sortio classifier, file content stays local and only filenames plus extracted entities are sent for matching, redacted from logs after 30 days. For closing docs with SSNs, we recommend Ollama or BYOK.",
  },
  {
    question: 'Does Sortio work with OneDrive, Dropbox, iCloud Drive, or Box?',
    answer:
      "Yes. Sortio is filesystem-transparent, so any folder visible in Finder or File Explorer works, including OneDrive, Dropbox, Box, Google Drive Desktop, and iCloud Drive. Many brokerages keep transactions in a shared OneDrive or Dropbox; Sortio routes new files into the right per-property folder regardless of the cloud sync underneath.",
  },
  {
    question: 'How does Sortio help a transaction coordinator managing 30 active deals?',
    answer:
      "TCs install Sortio on the laptop they use to triage incoming files. Each agent has their own filing convention; Sortio normalizes to a single broker-ready structure (per-property, role-based subfolders) so the TC reviews a consistent layout. Conflicting file assignments surface in a review queue rather than getting silently overwritten. Team plan adds a shared rule library so all TCs in the brokerage stay consistent.",
  },
  {
    question: 'Can Sortio organize MLS exports by tax year for end-of-year reporting?',
    answer:
      "Yes. The Rule Builder routes brokerage 1099s, commission disbursement statements, mileage logs, and home office receipts into a separate Tax Archive Space, organized by tax year. Closed transaction documents stay under their property folder for the deal record; the financial documents your CPA needs land in a clean per-year structure. At tax time, you hand over one folder.",
  },
  {
    question: 'How fast is it on a 460-photo MLS shoot?',
    answer:
      "Sortio Pro processes up to 5,000 files per AI sort. A 460-photo shoot typically completes in two to five minutes on a modern Mac, depending on the LLM mode. For multi-year listing photo archives (10,000 plus images), use the Rule Builder; rule-based sorts run unlimited files and never touch your AI allowance.",
  },
  {
    question: 'What does Sortio cost for a solo agent or a small TC team?',
    answer:
      "Free tier: a one-time AI trial allowance, enough to try a real closing folder. Pro: $14.99 per month or $99 per year, includes a monthly AI allowance sized for heavy use, AI sort up to 5,000 files per run, auto-sort on file change, and BYOK. Team: $29 per seat per month for brokerages and TC teams that want a shared rule library across agents.",
  },
  {
    question: 'Does it work on Windows or only Mac?',
    answer:
      "Sortio ships for macOS, Windows, and Linux. Most brokerages we work with are on Windows; many solo agents are on Mac. The dotloop integration (shipping) works identically on both platforms.",
  },
];

const FAQItem = ({ question, answer, isOpen, onToggle }) => (
  <div className="mb-4 transition-all duration-300 transform hover:translate-x-1">
    <div className={`apple-panel transition-all duration-300 ${isOpen ? 'ring-1 ring-sortio-primary/30' : ''}`}>
      <button className="flex justify-between items-center w-full p-5 text-left rounded-apple focus:outline-none" onClick={onToggle} aria-expanded={isOpen}>
        <span className="text-base font-medium text-apple-50 pr-4">{question}</span>
        <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-colors duration-300 ${isOpen ? 'bg-sortio-primary/20' : 'bg-apple-600/40'}`}>
          {isOpen ? <ChevronUp className="w-3.5 h-3.5 text-sortio-primary" /> : <ChevronDown className="w-3.5 h-3.5 text-apple-300" />}
        </div>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="p-5 pt-0">
          <p className="text-apple-300 text-sm leading-relaxed">{answer}</p>
        </div>
      </div>
    </div>
  </div>
);

const RealEstateFAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: { '@type': 'Answer', text: faq.answer },
    })),
  };

  return (
    <section id="real-estate-faq" className="my-20">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="flex flex-col items-center mb-12">
        <div className="mb-4 rounded-full bg-apple-700/60 backdrop-blur-sm p-2 border border-apple-600/20">
          <HelpCircle className="w-6 h-6 text-sortio-primary" />
        </div>
        <h2 className="text-3xl sm:text-4xl font-semibold mb-4 tracking-tight text-apple-50 text-center">
          Real estate <span className="apple-gradient-text">FAQ</span>
        </h2>
        <p className="text-apple-300 text-lg font-light max-w-2xl text-center">
          Questions from solo agents, transaction coordinators, and small brokerages weighing
          Sortio for per-property filing and dotloop workflows.
        </p>
      </div>

      <div className="max-w-3xl mx-auto">
        {FAQS.map((faq, index) => (
          <FAQItem key={index} question={faq.question} answer={faq.answer} isOpen={openIndex === index} onToggle={() => setOpenIndex(openIndex === index ? -1 : index)} />
        ))}
      </div>
    </section>
  );
};

export default RealEstateFAQ;
